import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import AIModalFileChooser, {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SelectSearchableMenu from 'ui-library/menus/SelectSearchableMenu';
import {ModalButton} from 'ui-library/modals/Modal';

export function getSelectTablesAndFilesModalButtons({
  handleNext,
  isDisabled,
}: {
  handleNext: () => void;
  isDisabled: boolean;
}): Array<ModalButton> {
  return [
    {
      text: 'Next',
      style: ButtonStyle.PRIMARY,
      onClick: handleNext,
      disabled: isDisabled,
    },
  ];
}

interface SelectTablesAndFilesStepProps {
  selectedFiles: Array<AIModalSelectedFile>;
  onSetSelectedFiles: (files: Array<AIModalSelectedFile>) => void;
  structuredTables: Array<StructuredTable<unknown, unknown>>;
  selectedTableNames: Array<string>;
  setSelectedTableNames: (tableNames: Array<string>) => void;
  errorMessage: string | null;
}

/**
 * First step of Data Entry Agent modal, where the user selects tables and uploads files.
 */
export default function SelectTablesAndFilesStep({
  selectedFiles,
  onSetSelectedFiles,
  structuredTables,
  selectedTableNames,
  setSelectedTableNames,
  errorMessage,
}: SelectTablesAndFilesStepProps): JSX.Element {
  const handleSetSelectedTableNames = useCallback(
    (tableNames: Array<string>) => {
      setSelectedTableNames(tableNames);
    },
    [setSelectedTableNames]
  );

  const optionDatas: Array<OptionData> = useMemo(
    () =>
      structuredTables.map((table) => ({
        name: table.name,
        value: table.name,
        icon: 'check',
      })),
    [structuredTables]
  );

  return (
    <div>
      <div className={css(styles.firstSectionTitle)}>Select tables</div>
      <SelectSearchableMenu
        optionDatas={optionDatas}
        selectedValues={selectedTableNames}
        onSelect={handleSetSelectedTableNames}
        noneSelectedMessage="Pick one or more tables"
        menuProps={{
          className: css(styles.tableSelector),
          isMulti: true,
          scrollableClassName: css(styles.tableSelectorScrollable),
          showSelectedAsChips: true,
        }}
      />
      <div className={css(styles.sectionTitle)}>Upload files</div>
      <AIModalFileChooser
        selectedFiles={selectedFiles}
        onSetSelectedFiles={onSetSelectedFiles}
        messageText="Attach any documents, spreadsheets, presentations, or images:"
      />
      {errorMessage && (
        <div className={css(styles.errorMessage)}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  firstSectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginBottom: 4,
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginTop: 15,
    marginBottom: 4,
  },
  errorMessage: {
    marginTop: 4,
    marginBottom: 4,
  },

  tableSelector: {
    border: `1px solid ${Colors.GRAY}`,
    boxShadow: 'none',
    position: 'relative',
    top: 0,
  },
  tableSelectorScrollable: {
    maxHeight: '200px !important',
  },
});
